import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  SelectChangeEvent,
  Stack
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import { useToast } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { clearAndonWorkstationsAndGrids } from "~/features/andon/andon.slice";
import {
  settingsInputMinHeightPx,
  settingsSelectSx,
  settingsSiteSelectMaxWidthPx
} from "~/features/settings/settings.styles";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { updateUsersFulfillmentCenter } from "~/redux/actions/store";
import { setSiteFcId, setThisWorkstation } from "~/redux/actions/workstations";
import { selectSiteFulfillmentCenterId } from "~/redux/selectors/siteSelectors";
import { useGetAllFulfillmentCentersQuery } from "~/redux/warehouse/fulfillmentCenter.hooks";
import { warehouseApi } from "~/redux/warehouse/warehouseApi";

import { Guid } from "~/types/api";

export const FulfillmentCenterSelect = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { errorToast } = useToast();
  const [selectedFulfillmentCenterId, setSelectedFulfillmentCenterId] =
    useState<Guid | null>(null);
  const fulfillmentCenterId = useAppSelector(selectSiteFulfillmentCenterId);
  useEffect(() => {
    setSelectedFulfillmentCenterId(fulfillmentCenterId);
  }, [fulfillmentCenterId]);
  const {
    isFetching: fulfillmentCentersFetching,
    data: fulfillmentCenters,
    error: fulfillmentCentersFetchError,
    refetch: refetchFulfillmentCenters
  } = useGetAllFulfillmentCentersQuery();

  const handleFulfillmentCenterChange = (event: SelectChangeEvent) => {
    const nextId = event.target.value;
    setSelectedFulfillmentCenterId(nextId === "" ? null : nextId);
  };

  const handleAssignFulfillmentCenter = async () => {
    if (!selectedFulfillmentCenterId) {
      return;
    }
    try {
      const selectedFc = fulfillmentCenters?.find(
        (fc) => fc.fulfillmentCenterId === selectedFulfillmentCenterId
      );
      dispatch(
        setSiteFcId(selectedFulfillmentCenterId, selectedFc?.abbreviation)
      );
      dispatch(clearAndonWorkstationsAndGrids());
      await dispatch(setThisWorkstation(null, true));
      await dispatch(updateUsersFulfillmentCenter(selectedFulfillmentCenterId));

      dispatch(
        warehouseApi.util.invalidateTags([
          "autostore grid",
          "workstation",
          "bin configuration"
        ])
      );
      const fcParam = selectedFc?.abbreviation
        ? `?fc=${encodeURIComponent(selectedFc.abbreviation)}`
        : "";
      window.location.href = `${window.location.pathname}${fcParam}`;
    } catch (err) {
      errorToast(getMessageFromRtkError(err));
    }
  };

  if (fulfillmentCentersFetching) {
    return (
      <Box sx={{ width: "100%" }}>
        <Skeleton variant="rectangular" />
      </Box>
    );
  }

  if (fulfillmentCentersFetchError) {
    return (
      <Alert
        variant="outlined"
        severity="error"
        action={
          <Button onClick={refetchFulfillmentCenters}>{t("retry")}</Button>
        }
      >
        {getMessageFromRtkError(fulfillmentCentersFetchError)}
      </Alert>
    );
  }

  return (
    <>
      {fulfillmentCenters != null && fulfillmentCenters.length > 0 ? (
        <Stack spacing={1} sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              flexWrap: { md: "nowrap" },
              alignItems: { md: "center" },
              width: "100%",
              minWidth: 0,
              columnGap: { xs: 0, md: 2 },
              rowGap: { xs: 1.5, md: 0 }
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: {
                  xs: "100%",
                  md: settingsSiteSelectMaxWidthPx
                },
                minWidth: 0,
                flex: { md: "0 1 auto" }
              }}
            >
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                aria-label="fulfillment-center"
                id="fulfillment-center"
              >
                <Select
                  labelId="fulfillment-center"
                  size="small"
                  variant="outlined"
                  aria-label="fulfillment-center-select-label"
                  aria-describedby="fulfillment-center-select-helper"
                  sx={settingsSelectSx}
                  value={
                    selectedFulfillmentCenterId != null &&
                    (fulfillmentCenters ?? []).some(
                      (fc) =>
                        fc.fulfillmentCenterId === selectedFulfillmentCenterId
                    )
                      ? selectedFulfillmentCenterId
                      : ""
                  }
                  onChange={handleFulfillmentCenterChange}
                >
                  {(fulfillmentCenters ?? []).map((fc) => (
                    <MenuItem
                      key={fc.fulfillmentCenterId}
                      value={fc.fulfillmentCenterId}
                    >
                      {fc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-end", md: "flex-start" },
                alignItems: "center",
                flexShrink: 0,
                minWidth: 0
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                disabled={!selectedFulfillmentCenterId}
                onClick={() => void handleAssignFulfillmentCenter()}
                aria-label="assign-fulfillment-center"
                sx={{
                  flexShrink: 0,
                  minHeight: settingsInputMinHeightPx,
                  boxSizing: "border-box"
                }}
              >
                {t("assign")}
              </Button>
            </Box>
          </Box>
          <FormHelperText id="fulfillment-center-select-helper" sx={{ m: 0 }}>
            {t("select fc label")}
          </FormHelperText>
        </Stack>
      ) : null}
    </>
  );
};
